/**
 * Macro schedule import — shown above the console for P6 / MS Project.
 *
 * Lists what the source holds, then pulls one project in as a Panorix macro
 * schedule. Reused verbatim by Project Controls so the button there and the
 * console here cannot drift.
 */
import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Download, Loader2, RefreshCw, CalendarRange } from 'lucide-react';

export const ScheduleImportPanel = ({ connector, label, portfolioProjectId, onImported }) => {
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [pid, setPid] = useState(portfolioProjectId || '');
  const [replace, setReplace] = useState(true);
  const [manualId, setManualId] = useState('');

  const list = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/connectors/schedule/${connector}/projects`);
      setProjects(data.projects || []);
      if (!data.projects?.length) toast.info('The source returned no projects.');
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not reach the source — configure and test the connection below first.');
      setProjects([]);
    } finally { setLoading(false); }
  };

  const run = async (externalId, name) => {
    if (!pid.trim()) {
      toast.error('Enter the Panorix project id the schedule belongs to.');
      return;
    }
    setImporting(externalId);
    try {
      const { data } = await api.post(`/connectors/schedule/${connector}/import`, {
        externalId, name, portfolioProjectId: pid.trim(), replace,
      });
      toast.success(`${data.imported} activities imported into the macro schedule`);
      onImported?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setImporting(null); }
  };

  return (
    <div className="glass-card p-4" data-testid="schedule-import-panel">
      <div className="flex items-center gap-2 mb-3">
        <CalendarRange className="w-4 h-4 text-emerald-500" />
        <h3 className="text-sm font-semibold">Import macro schedule</h3>
        <Badge variant="secondary" className="text-[10px]">{label}</Badge>
        <Button size="sm" variant="outline" className="ml-auto h-7 text-xs gap-1"
                onClick={list} disabled={loading} data-testid="schedule-import-list">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          List source projects
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">
            Panorix project id
          </Label>
          <Input value={pid} onChange={e => setPid(e.target.value)} className="h-8 text-xs"
                 placeholder="proj_001" data-testid="schedule-import-pid" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">
            Source project id (if you know it)
          </Label>
          <div className="flex gap-1.5">
            <Input value={manualId} onChange={e => setManualId(e.target.value)}
                   className="h-8 text-xs" placeholder="e.g. 4815"
                   data-testid="schedule-import-manual-id" />
            <Button size="sm" className="h-8 text-xs" disabled={!manualId.trim() || !!importing}
                    onClick={() => run(manualId.trim(), `${label} ${manualId.trim()}`)}
                    data-testid="schedule-import-manual-go">
              Import
            </Button>
          </div>
        </div>
        <label className="flex items-end gap-2 text-xs pb-1.5 cursor-pointer">
          <Checkbox checked={replace} onCheckedChange={v => setReplace(!!v)}
                    data-testid="schedule-import-replace" />
          Replace the existing activities on that schedule
        </label>
      </div>

      {projects?.length > 0 && (
        <div className="border border-white/[0.06] rounded-lg overflow-hidden"
             data-testid="schedule-import-table">
          <table className="w-full text-[11px]">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-1.5">Code</th>
                <th className="text-left px-3 py-1.5">Name</th>
                <th className="text-left px-3 py-1.5 w-28">Data date</th>
                <th className="px-3 py-1.5 w-24" />
              </tr>
            </thead>
            <tbody>
              {projects.map(p => (
                <tr key={p.externalId} className="border-t border-white/[0.04]">
                  <td className="px-3 py-1.5 font-mono text-[10px]">{p.code || p.externalId}</td>
                  <td className="px-3 py-1.5">{p.name || '—'}</td>
                  <td className="px-3 py-1.5 tabular-nums">
                    {p.dataDate ? String(p.dataDate).slice(0, 10) : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1"
                            disabled={!!importing}
                            onClick={() => run(p.externalId, p.name)}
                            data-testid={`schedule-import-go-${p.externalId}`}>
                      {importing === p.externalId
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Download className="w-3 h-3" />}
                      Import
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {projects?.length === 0 && (
        <p className="text-[11px] text-slate-500" data-testid="schedule-import-none">
          Nothing came back. Configure the connection in Settings → Connectors, press
          Test connection there, then list again.
        </p>
      )}
    </div>
  );
};

export default ScheduleImportPanel;
